import { Link } from "react-router-dom";
import { ArrowRight, Check, BadgePercent } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useLang } from "../context/LanguageContext";

const INCLUDED = [
  "Billettsalg med ditt eget Vipps-oppsett",
  "QR-innsjekk med mobilen",
  "Billettyper, rabattkoder og venteliste",
  "Deltakerliste og kjøperoversikt (PDF/CSV)",
  "Automatiske billetter og påminnelser på e-post",
  "Ukesrapport med salg, visninger og innsjekkinger",
  "Plakater med QR-kode (A4/A3)",
  "Salgsrapport klar for regnskapet",
];

const FAQ = [
  {
    q: "Hva koster Right in?",
    a: "2,9 % per solgte billett, med et minimum på 99 kr per gjennomførte arrangement. Det er hele prislisten – ingen etableringskostnad, ingen månedsavgift, ingen binding.",
  },
  {
    q: "Når må jeg betale?",
    a: "Ingen betaling før arrangementet er over. Du oppretter, publiserer og selger billetter uten å legge ut noe underveis – fakturaen kommer først i etterkant, når arrangementet er gjennomført. Et utkast som aldri publiseres, faktureres aldri.",
  },
  {
    q: "Hva om jeg tester systemet uten å publisere?",
    a: "Da betaler du ingenting. Fakturaen kommer først etter at et publisert arrangement er gjennomført – lav terskel for å prøve er en del av løftet vårt.",
  },
  {
    q: "Betaler publikum noe ekstra?",
    a: "Nei. Vi legger aldri gebyrpåslag på billettene. Prisen du setter er prisen publikum betaler. Avgiften på 2,9 % bæres av deg som arrangør.",
  },
  {
    q: "Hvordan foregår betalingen?",
    a: "Publikum betaler med Vipps gjennom ditt eget Vipps-oppsett, så pengene går rett til din konto – ikke via oss. Right in fakturerer deg for avgiften i etterkant, når arrangementet er over.",
  },
  {
    q: "Hva med gratisarrangementer?",
    a: "Billettene er selvsagt gratis for publikum, og 2,9 % av 0 kr er 0 kr. Minimumsbeløpet på 99 kr per gjennomførte arrangement gjelder også her – det dekker QR-billetter, innsjekk og rapporter for arrangementet.",
  },
  {
    q: "Er det bindingstid eller skjulte kostnader?",
    a: "Nei. Ingen binding, ingen faste kostnader, ingen moduler å kjøpe. Alle arrangører får hele verktøykassen fra dag én.",
  },
  {
    q: "Hvordan kommer jeg i gang?",
    a: "Registrer deg med organisasjonsnummeret ditt – vi henter navnet fra Brønnøysundregistrene. Deretter oppretter du arrangementet og publiserer når du er klar.",
  },
];

export default function PricingPage() {
  const { t } = useLang();
  return (
    <PublicLayout>
      {/* Price hero */}
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-left">
          <Badge variant="secondary" className="text-xs font-medium mb-5" data-testid="pricing-page-badge">
            <BadgePercent className="w-3.5 h-3.5 mr-1.5" />
            {t("Pris")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="pricing-page-title">
            {t("Én enkel pris.")}
          </h1>
          <p className="mt-5 font-heading font-semibold leading-none">
            <span className="text-6xl sm:text-7xl tabular-nums" data-testid="pricing-page-percent">2,9&nbsp;%</span>
            <span className="text-lg text-muted-foreground ml-3">{t("per solgte billett")}</span>
          </p>
          <p className="mt-3 text-muted-foreground" data-testid="pricing-page-minimum">
            {t("Minimum 99 kr per gjennomførte arrangement. Ingen betaling før arrangementet er over.")}
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button asChild size="lg" className="h-12 px-6" data-testid="pricing-page-cta">
              <Link to="/registrer">
                {t("Opprett arrangement")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="secondary" className="h-12 px-6" data-testid="pricing-page-cta-landing">
              <Link to="/for-arrangorer">{t("Les mer om Right in")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Included */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-6">
          {t("Alt er inkludert")}
        </h2>
        <Card className="border-border">
          <CardContent className="p-6">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="pricing-included-list">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  {t(item)}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="border-y border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-6" data-testid="pricing-faq-title">
            {t("Vanlige spørsmål om prisen")}
          </h2>
          <Accordion type="single" collapsible className="w-full" data-testid="pricing-faq-accordion">
            {FAQ.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} data-testid={`pricing-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-heading font-medium" data-testid={`pricing-faq-trigger-${i}`}>
                  {t(item.q)}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed" data-testid={`pricing-faq-content-${i}`}>
                  {t(item.a)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="rounded-2xl bg-ink text-ink-foreground px-6 sm:px-12 py-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">
              {t("Prøv Right in – uten risiko")}
            </h2>
            <p className="text-primary-foreground/75">
              {t("Opprett arrangementet i dag. Ingen betaling før arrangementet er over.")}
            </p>
          </div>
          <Button asChild size="lg" variant="secondary" className="h-12 px-6" data-testid="pricing-bottom-cta">
            <Link to="/registrer">
              {t("Prøv Right in")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
}
